import React from 'react';
import { 
  Award, 
  TrendingUp, 
  Users, 
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

function Dashboard({ data, onNewSearch }) {
  const { profileData, credibilityMetrics, reachMetrics, overallScores, aiInsights, recommendations } = data;

  const renderMetricCard = (title, metric) => {
    if (!metric || !metric.details) return null;

    return (
      <div className="metric-card" key={title}>
        <div className="metric-header">
          <div className="metric-title">{title}</div>
          <div className="metric-score">{metric.score || 0}/100</div>
        </div>
        <div className="metric-bar">
          <div 
            className="metric-fill" 
            style={{ width: `${metric.percentage || 0}%` }}
          ></div>
        </div>
        <div className="metric-details">
          {metric.details.reasoning || 'No details available'}
        </div>
      </div>
    );
  };

  const getRecommendationIcon = (type) => {
    switch(type) {
      case 'success': return <CheckCircle size={20} color="#10b981" />;
      case 'warning': return <AlertCircle size={20} color="#f59e0b" />;
      case 'info': return <Info size={20} color="#3b82f6" />;
      default: return <Info size={20} color="#3b82f6" />;
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="profile-info">
          <div className="profile-avatar">
            {profileData?.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="profile-details">
            <h2>@{profileData?.username || 'Unknown'}</h2>
            <p>{profileData?.platform || 'Social Media'}</p>
          </div>
        </div>
        <button className="new-search-btn" onClick={onNewSearch}>
          New Search
        </button>
      </div>

      <div className="scores-overview">
        <div className="score-card">
          <h3><Award size={24} style={{ marginBottom: '10px' }} /></h3>
          <div className="score-value">{overallScores?.credibility || 0}</div>
          <div className="score-label">Credibility Score</div>
        </div>
        <div className="score-card">
          <h3><TrendingUp size={24} style={{ marginBottom: '10px' }} /></h3>
          <div className="score-value">{overallScores?.reach || 0}</div>
          <div className="score-label">Reach Score</div>
        </div>
        <div className="score-card">
          <h3><Users size={24} style={{ marginBottom: '10px' }} /></h3>
          <div className="score-value">{overallScores?.overall || 0}</div>
          <div className="score-label">Overall Score</div>
        </div>
      </div>

      <div className="metrics-grid">
        <h3 style={{ gridColumn: '1 / -1', fontSize: '1.5rem', marginBottom: '10px' }}>
          Credibility Metrics
        </h3>
        {credibilityMetrics && Object.entries(credibilityMetrics).map(([key, value]) => 
          renderMetricCard(
            key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
            value
          )
        )}

        <h3 style={{ gridColumn: '1 / -1', fontSize: '1.5rem', marginBottom: '10px', marginTop: '20px' }}>
          Reach Metrics
        </h3>
        {reachMetrics && Object.entries(reachMetrics).map(([key, value]) => 
          renderMetricCard(
            key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
            value
          )
        )}
      </div>

      {aiInsights && (
        <div className="insights-section">
          <h3><MessageCircle size={24} style={{ display: 'inline', marginRight: '10px' }} />AI Insights</h3>
          <div style={{ marginTop: '20px' }}>
            <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#555' }}>
              {aiInsights.summary}
            </p>
            {aiInsights.strengths && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '10px', color: '#10b981' }}>✓ Strengths:</h4>
                <ul style={{ paddingLeft: '20px', lineHeight: '2' }}>
                  {aiInsights.strengths.map((strength, idx) => (
                    <li key={idx}>{strength}</li>
                  ))}
                </ul>
              </div>
            )}
            {aiInsights.weaknesses && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '10px', color: '#f59e0b' }}>⚠ Areas for Improvement:</h4>
                <ul style={{ paddingLeft: '20px', lineHeight: '2' }}>
                  {aiInsights.weaknesses.map((weakness, idx) => (
                    <li key={idx}>{weakness}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {recommendations && recommendations.length > 0 && (
        <div className="insights-section">
          <h3>Recommendations</h3>
          <div className="recommendations">
            {recommendations.map((rec, idx) => (
              <div key={idx} className={`recommendation ${rec.type}`}>
                {getRecommendationIcon(rec.type)}
                <span>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
